import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntypedFormControl } from '@angular/forms';
import { ENTER } from '@angular/cdk/keycodes';
import { SelectedItem } from '../../../store/constants';
import { removeInputElement } from '../../../common/utils';
import { AutoUnsubscribe } from '../../../common/decorators';
import { Subscription, timer } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import { isEqual } from 'lodash';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { IconNames } from '../../../common/constants/icon-names';
import { IntrackFeatureModule } from '../../../intrack-feature.module';
import { SvgIconComponent } from '../../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-chips-with-search-generic',
  imports: [CommonModule, IntrackFeatureModule, SvgIconComponent],
  templateUrl: './chips-with-search-generic.component.html',
  styleUrls: ['./chips-with-search-generic.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@AutoUnsubscribe
export class ChipsWithSearchComponentGeneric<T> implements OnInit {
  @Input() label: string;
  @Input() isIconVisible = true;
  @Input() removable = true;
  @Input() optionNameKeys: string[];
  @Input() panelWidth?: string | number;
  @Input() set itemOptions(items: T[]) {
    if (items) {
      this.filteredItems = this.filterOptions(this.selectedItemsControl, items);
    }
  }
  @Input() selectedItemsControl: UntypedFormControl;
  @Input() selectedItems: T[];
  @Output() fetchItemsOptions = new EventEmitter<string>();
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  itemInputControl: UntypedFormControl = new UntypedFormControl('');
  separatorKeysCodes: number[] = [ENTER];
  filteredItems: T[] = [];
  subscriptions: Subscription[] = [];
  removeElement = removeInputElement;
  iconNames = IconNames;

  ngOnInit(): void {
    this.initInputControl(this.itemInputControl, this.fetchItemsOptions);
  }

  addOptionElement(control: UntypedFormControl, input: HTMLInputElement, inputControl: UntypedFormControl): void {
    const { value } = inputControl;
    if (value) {
      control.setValue(control.value.concat(value));
      control.markAsDirty();
    }
    if (input) {
      input.value = '';
    }
    inputControl.setValue(null);
    this.resetOptions();
  }

  onInputBlur(input: HTMLInputElement): void {
    timer(100)
      .pipe(take(1))
      .subscribe(() => {
        input.value = '';
        this.resetOptions();
      });
  }

  private filterOptions(selectedOptionsControl: UntypedFormControl, allOptions: T[]): T[] {
    const value: SelectedItem<T>[] = (selectedOptionsControl && selectedOptionsControl.value) || [];
    const selectedOptions: T[] = value.map((item: SelectedItem<T>) => {
      const tempItem: SelectedItem<T> = { ...item };
      delete tempItem.selected;
      return tempItem;
    });

    return allOptions.filter(
      (option: T) => !selectedOptions.some((selectedOption: T) => isEqual(option, selectedOption)),
    );
  }

  private initInputControl(control: UntypedFormControl, emitter: EventEmitter<string>): void {
    this.subscriptions.push(
      control.valueChanges.pipe(debounceTime(500), filter(Boolean)).subscribe((value: string) => {
        if (value.length === 0) {
          this.resetOptions();
          this.autocomplete.closePanel();
        } else {
          emitter.emit(value);
        }
      }),
    );
  }

  private resetOptions(): void {
    this.filteredItems = [];
  }
}
